<template>
    <div class="flex flex-col h-full px-6 py-4">
        <div class="flex items-center justify-between mb-4">
            <span class="font-bold text-xl">Kontoavstemming</span>
            <UFormField label="Periode">
                <USelectMenu
                    v-model="selectedPeriod"
                    :items="periods"
                    value-key="value"
                    label-key="label"
                />
            </UFormField>
        </div>
        <div class="flex-1 min-h-0">
            <ag-grid-vue
                class="h-full"
                :theme="agTheme"
                :row-data="rows"
                :column-defs="colDefs"
                :grid-options="gridOptions"
                :pinned-bottom-row-data="pinnedBottomRowData"
                @grid-ready="onGridReady"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import {computed, onMounted, ref, shallowRef, watch} from 'vue'
import {AgGridVue} from 'ag-grid-vue3'
import type {ColDef, GridApi, GridOptions, GridReadyEvent, ICellRendererParams} from 'ag-grid-community'
import {agTheme} from '@/agUtils/ag-theme.service'
import {globalDefaultColDef} from '@/agUtils'
import {formatCurrency} from '@/services/formatter.service'
import {format, startOfMonth, endOfMonth} from 'date-fns'
import {nb} from 'date-fns/locale'
import {findAccountByNo} from '@/bookkeeping-account/bookkeepingAccount.service'
import {getGeneralLedger} from '@/genleg/ledger.service'
import {listReconciliations, saveReconciliation, updateReconciliation} from './accountReconciliation.service'
import type {AccountReconciliation} from './accountReconciliation'
import type {LedgerAccount} from '@/types/reports/LedgerAccount'
import {notifyError, notifySuccess} from '@/services/infrastructure/notification.service'
import ReconciliationVedleggCell from './ReconciliationVedleggCell.vue'

// ── Period picker ────────────────────────────────────────────────────────────

const currentYear = new Date().getFullYear()
const periods = Array.from({length: 12}, (_, i) => {
    const date = new Date(currentYear, i, 1)
    return {
        value: format(date, 'yyyy-MM'),
        label: format(date, 'MMMM yyyy', {locale: nb}),
    }
})
const selectedPeriod = ref(format(new Date(), 'yyyy-MM'))

const periodYear = computed(() => Number(selectedPeriod.value.split('-')[0]))
const periodMonth = computed(() => Number(selectedPeriod.value.split('-')[1]))
const fromDate = computed(() => format(startOfMonth(new Date(periodYear.value, periodMonth.value - 1, 1)), 'yyyy-MM-dd'))
const toDate = computed(() => format(endOfMonth(new Date(periodYear.value, periodMonth.value - 1, 1)), 'yyyy-MM-dd'))

// ── Types ─────────────────────────────────────────────────────────────────────

type ReconciliationRow = {
    accountNo: string
    accountName: string
    ledgerBalance: number      // centimal
    bankBalance: number        // centimal (0 if not set)
    bankBalanceInput: string   // editable string
    deviation: number          // centimal
    comment: string
    documentMetaUuid: string | null
    reconciliationUuid: string | null
    saving: boolean
}

export type ReconciliationVedleggCellParams = {
    value: string | null
    data: ReconciliationRow
    context: {
        onDocumentUpdated: (row: ReconciliationRow, documentMetaUuid: string | null) => void
    }
}

// ── Data ─────────────────────────────────────────────────────────────────────

const rows = ref<ReconciliationRow[]>([])
const gridApi = shallowRef<GridApi>()

const pinnedBottomRowData = computed(() => {
    if (rows.value.length === 0) return []
    const totalLedger = rows.value.reduce((sum, r) => sum + r.ledgerBalance, 0)
    const totalBank = rows.value.reduce((sum, r) => sum + r.bankBalance, 0)
    return [{
        accountNo: `${rows.value.filter(r => r.reconciliationUuid !== null).length} av ${rows.value.length} kontoer avstemt`,
        accountName: '',
        ledgerBalance: totalLedger,
        bankBalance: totalBank,
        deviation: totalBank - totalLedger,
        _isPinned: true,
    }]
})

const loadData = async () => {
    const [ledger, reconciliations] = await Promise.all([
        getGeneralLedger({fromDate: fromDate.value, toDate: toDate.value}),
        listReconciliations(undefined, periodYear.value),
    ])
    if (!ledger) return

    const recMap = new Map<string, AccountReconciliation>()
    for (const r of reconciliations) {
        if (r.periodType === 'MONTH' && r.periodMonth === periodMonth.value) {
            recMap.set(r.accountNo, r)
        }
    }

    rows.value = ledger.ledgerAccounts.map((la: LedgerAccount) => {
        const rec = recMap.get(la.accountNo)
        const ledgerBalance = Math.round(la.outgoingBalance * 100)
        const bankBalance = rec ? rec.bankBalance : 0
        return {
            accountNo: la.accountNo,
            accountName: findAccountByNo(la.accountNo)?.name ?? '',
            ledgerBalance,
            bankBalance,
            bankBalanceInput: rec ? String(rec.bankBalance / 100) : '',
            deviation: bankBalance - ledgerBalance,
            comment: rec?.comment ?? '',
            documentMetaUuid: rec?.documentMetaUuid ?? null,
            reconciliationUuid: rec?.uuid ?? null,
            saving: false,
        }
    })
}

onMounted(loadData)
watch(selectedPeriod, loadData)

// ── Save ─────────────────────────────────────────────────────────────────────

const saveRow = async (row: ReconciliationRow) => {
    const parsed = parseFloat(row.bankBalanceInput.replace(',', '.'))
    if (isNaN(parsed) && row.bankBalanceInput !== '') return
    const bankBalance = isNaN(parsed) ? 0 : Math.round(parsed * 100)
    row.saving = true
    try {
        if (row.reconciliationUuid) {
            const updated = await updateReconciliation(row.accountNo, row.reconciliationUuid, {
                bankBalance,
                comment: row.comment || null,
                documentMetaUuid: row.documentMetaUuid,
            })
            if (updated) {
                row.bankBalance = updated.bankBalance
                row.deviation = bankBalance - row.ledgerBalance
            }
        } else {
            const saved = await saveReconciliation(row.accountNo, {
                periodType: 'MONTH',
                periodYear: periodYear.value,
                periodMonth: periodMonth.value,
                bankBalance,
                comment: row.comment || null,
                documentMetaUuid: row.documentMetaUuid,
            })
            if (saved) {
                row.reconciliationUuid = saved.uuid
                row.bankBalance = saved.bankBalance
                row.deviation = bankBalance - row.ledgerBalance
            }
        }
        notifySuccess({title: 'Lagret', description: `Konto ${row.accountNo} avstemt.`})
    } catch {
        notifyError({title: 'Feil', description: 'Klarte ikke å lagre kontoavstemming.'})
    } finally {
        row.saving = false
        gridApi.value?.refreshCells({rowNodes: undefined, force: true})
    }
}

const onDocumentUpdated = async (row: ReconciliationRow, documentMetaUuid: string | null) => {
    row.documentMetaUuid = documentMetaUuid
    await saveRow(row)
}

// ── Grid ──────────────────────────────────────────────────────────────────────

const onGridReady = (e: GridReadyEvent) => {
    gridApi.value = e.api
}

const gridOptions: GridOptions = {
    getRowId: ({data}) => data.accountNo,
    enableCellTextSelection: true,
    suppressCellFocus: true,
    rowHeight: 52,
    defaultColDef: {
        ...globalDefaultColDef,
        sortable: true,
        filter: false,
        resizable: true,
    },
    getRowClass: ({data}) => data?._isPinned ? '!bg-base-200 font-bold' : undefined,
    context: {
        onDocumentUpdated,
    },
}

const colDefs: ColDef[] = [
    {
        headerName: 'Konto',
        field: 'accountNo',
        width: 90,
        sortable: true,
        sort: 'asc',
    },
    {
        headerName: 'Kontonavn',
        field: 'accountName',
        flex: 2,
    },
    {
        headerName: 'Utg. saldo',
        field: 'ledgerBalance',
        flex: 1,
        type: 'rightAligned',
        cellRenderer: ({value, data}: ICellRendererParams) =>
            data?._isPinned
                ? `<span class="font-bold">${formatCurrency(value / 100)}</span>`
                : formatCurrency(value / 100),
    },
    {
        headerName: 'Avstemt',
        field: 'bankBalanceInput',
        flex: 1,
        type: 'rightAligned',
        editable: ({data}) => !data?._isPinned,
        cellRenderer: ({value, data}: ICellRendererParams) => {
            if (data?._isPinned) return `<span class="font-bold">${formatCurrency(data.bankBalance / 100)}</span>`
            const display = value !== '' ? value : '0,00'
            return `<span class="text-muted italic">${display}</span>`
        },
        onCellValueChanged: ({data}: any) => saveRow(data),
    },
    {
        headerName: 'Avvik',
        field: 'deviation',
        flex: 1,
        type: 'rightAligned',
        cellRenderer: ({value, data}: ICellRendererParams) => {
            if (data?._isPinned) return `<span class="font-bold">${formatCurrency(value / 100)}</span>`
            const formatted = formatCurrency(value / 100)
            const color = value !== 0 ? 'color: rgb(239,68,68)' : 'color: rgb(22,163,74)'
            return `<span style="${color}">${formatted}</span>`
        },
    },
    {
        headerName: 'Kommentar',
        field: 'comment',
        flex: 2,
        editable: ({data}) => !data?._isPinned,
        cellRenderer: ({value, data}: ICellRendererParams) => {
            if (data?._isPinned) return ''
            return value ? value : '<span class="text-muted italic">Kommentar...</span>'
        },
        onCellValueChanged: ({data}: any) => saveRow(data),
    },
    {
        headerName: 'Vedlegg',
        field: 'documentMetaUuid',
        width: 70,
        cellRenderer: ReconciliationVedleggCell,
        cellRendererParams: ({data}: ICellRendererParams) => ({
            value: data?._isPinned ? null : data?.documentMetaUuid ?? null,
        }),
        cellStyle: {display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0'},
    },
    {
        headerName: 'Status',
        field: 'reconciliationUuid',
        width: 150,
        cellRenderer: ({value, data}: ICellRendererParams) => {
            if (data?._isPinned) return ''
            if (value === null) {
                return '<span style="padding:1px 7px;border-radius:9999px;font-size:0.72rem;white-space:nowrap;background:transparent;color:var(--color-text-muted);border:1px solid var(--color-border)">Ikke avstemt</span>'
            }
            if (data?.deviation !== 0) {
                return '<span style="padding:1px 7px;border-radius:9999px;font-size:0.72rem;white-space:nowrap;background:rgb(255,247,237);color:rgb(194,120,3);border:1px solid rgb(253,186,116)">⚠ Med avvik</span>'
            }
            return '<span style="padding:1px 7px;border-radius:9999px;font-size:0.72rem;white-space:nowrap;background:rgb(220,252,231);color:rgb(22,163,74);border:1px solid rgb(134,239,172)">✓ Avstemt</span>'
        },
        cellStyle: {display: 'flex', alignItems: 'center'},
    },
]
</script>

<style scoped>
</style>
